export type BusinessVerticalId =
  | "restaurant"
  | "barber_shop"
  | "generic_local_business";

export type BusinessModuleId =
  | "overview"
  | "analytics"
  | "leads"
  | "marketing"
  | "guestDirectory"
  | "support"
  | "staff"
  | "settings"
  | "announcements"
  | "checklists"
  | "orderingSettings"
  | "deliveryZones"
  | "reservations"
  | "menu"
  | "orders"
  | "redemptions"
  | "events"
  | "catering"
  | "privateEvents"
  | "appointments"
  | "services"
  | "barbers"
  | "aiVoice"
  | "gbpAgent"
  | "social"
  | "tips";

export type BusinessVocabulary = {
  customerSingular: string;
  customerPlural: string;
  leadSingular: string;
  leadPlural: string;
  bookingSingular: string;
  bookingPlural: string;
  staffSingular: string;
  staffPlural: string;
  serviceSingular: string;
  servicePlural: string;
  campaignSingular: string;
  campaignPlural: string;
};

export type BusinessVerticalManifest = {
  id: BusinessVerticalId;
  label: string;
  description: string;
  schemaType:
    | "Restaurant"
    | "HealthAndBeautyBusiness"
    | "LocalBusiness";
  defaultModules: BusinessModuleId[];
  vocabulary: BusinessVocabulary;
  moduleLabels: Partial<Record<BusinessModuleId, string>>;
  publicChrome: {
    primaryActionLabel: string;
    primaryActionKind:
      | "reservation"
      | "appointment"
      | "contact";
    primaryActionHref: string;
    serviceLabel: string;
    serviceHref: string;
    peopleLabel: string;
    peopleHref: string;
  };
};

const sharedGrowthModules: BusinessModuleId[] = [
  "overview",
  "analytics",
  "leads",
  "marketing",
  "gbpAgent",
  // Ships in the core package, like gbpAgent — every vertical owns a social
  // presence, so it is shared rather than package-gated.
  "social",
  "guestDirectory",
  "support",
  "staff",
  "settings",
  "announcements",
];

export const BUSINESS_VERTICALS: Record<
  BusinessVerticalId,
  BusinessVerticalManifest
> = {
  restaurant: {
    id: "restaurant",
    label: "Restaurant",
    description:
      "Food-service businesses with menus, reservations, ordering, guest management, events, and hospitality marketing.",
    schemaType: "Restaurant",
    defaultModules: [
      ...sharedGrowthModules,
      "reservations",
      "menu",
      "orders",
      "redemptions",
      "events",
      "catering",
      "privateEvents",
      "checklists",
      "orderingSettings",
      "deliveryZones",
      "aiVoice",
      // QR staff tipping stays on BusinessModuleId so leftover admin pages
      // type-check, but it is off the restaurant package: no vertical ships it.
    ],
    vocabulary: {
      customerSingular: "Guest",
      customerPlural: "Guests",
      leadSingular: "Inquiry",
      leadPlural: "Inquiries",
      bookingSingular: "Reservation",
      bookingPlural: "Reservations",
      staffSingular: "Staff member",
      staffPlural: "Staff",
      serviceSingular: "Menu item",
      servicePlural: "Menu",
      campaignSingular: "Campaign",
      campaignPlural: "Campaigns",
    },
    moduleLabels: {
      reservations: "Reservations",
      menu: "Menu",
      orders: "Orders",
      // Covers offer coupons AND checkout promo codes, so the row reads
      // "Discounts"; the Offers tab keeps the narrower redemption wording.
      redemptions: "Discounts",
      events: "Event RSVPs",
      catering: "Catering",
      privateEvents: "Event CRM",
      orderingSettings: "Ordering Settings",
      deliveryZones: "Delivery Zones",
      guestDirectory: "Guest CRM",
      gbpAgent: "GBP AI Agent",
      support: "Inbox",
      checklists: "Checklist",
      analytics: "Analytics",
    },
    publicChrome: {
      primaryActionLabel: "Reserve a Table",
      primaryActionKind: "reservation",
      primaryActionHref: "/reservations",
      serviceLabel: "Menu",
      serviceHref: "/menu",
      peopleLabel: "About",
      peopleHref: "/about",
    },
  },
  barber_shop: {
    id: "barber_shop",
    label: "Barber Shop",
    description:
      "Appointment-based grooming businesses with services, staff calendars, client history, and local growth workflows.",
    schemaType: "HealthAndBeautyBusiness",
    defaultModules: [
      ...sharedGrowthModules,
      "appointments",
      "services",
      "barbers",
    ],
    vocabulary: {
      customerSingular: "Client",
      customerPlural: "Clients",
      leadSingular: "Booking request",
      leadPlural: "Booking Requests",
      bookingSingular: "Appointment",
      bookingPlural: "Appointments",
      staffSingular: "Barber",
      staffPlural: "Barbers",
      serviceSingular: "Service",
      servicePlural: "Services",
      campaignSingular: "Promotion",
      campaignPlural: "Promotions",
    },
    moduleLabels: {
      leads: "Booking Requests",
      guestDirectory: "Clients",
      services: "Services",
      staff: "Barbers",
      appointments: "Appointments",
      barbers: "Barbers",
      marketing: "Promotions",
    },
    publicChrome: {
      primaryActionLabel: "Book Appointment",
      primaryActionKind: "appointment",
      primaryActionHref: "/contact",
      serviceLabel: "Services",
      serviceHref: "/about",
      peopleLabel: "Barbers",
      peopleHref: "/about",
    },
  },
  generic_local_business: {
    id: "generic_local_business",
    label: "Local Business",
    description:
      "Fail-closed default for businesses that have not been mapped to a specialized vertical yet.",
    schemaType: "LocalBusiness",
    defaultModules: sharedGrowthModules,
    vocabulary: {
      customerSingular: "Customer",
      customerPlural: "Customers",
      leadSingular: "Lead",
      leadPlural: "Leads",
      bookingSingular: "Booking",
      bookingPlural: "Bookings",
      staffSingular: "Staff member",
      staffPlural: "Staff",
      serviceSingular: "Service",
      servicePlural: "Services",
      campaignSingular: "Campaign",
      campaignPlural: "Campaigns",
    },
    moduleLabels: {
      leads: "Leads",
      guestDirectory: "Customers",
      support: "Inbox",
      checklists: "Checklist",
    },
    publicChrome: {
      primaryActionLabel: "Contact Us",
      primaryActionKind: "contact",
      primaryActionHref: "/contact",
      serviceLabel: "Services",
      serviceHref: "/about",
      peopleLabel: "About",
      peopleHref: "/about",
    },
  },
};

export function getBusinessVertical(
  verticalId: BusinessVerticalId | undefined,
): BusinessVerticalManifest {
  if (!verticalId) return BUSINESS_VERTICALS.generic_local_business;
  return (
    BUSINESS_VERTICALS[verticalId] ?? BUSINESS_VERTICALS.generic_local_business
  );
}

export function getModuleLabel(
  manifest: BusinessVerticalManifest,
  moduleId: BusinessModuleId,
  fallback: string,
): string {
  return manifest.moduleLabels[moduleId] ?? fallback;
}
